import React, { useEffect, useState } from 'react'
import SbEditable from 'storyblok-react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Sentry from '@sentry/react'
import InputMask from 'react-input-mask'
import * as Yup from 'yup'
import moment from 'moment-timezone';
import fedHolidays from '@18f/us-federal-holidays';

import { leads, metrics } from '../../services'
import Components from '../components.js'
import TrackFormDirty from '../TrackFormDirty'
import { PhoneRegex } from '../../constants'
import './lead-form-v3.scss'
import CallInIcon from './callInIcon.png';
import CallOutIcon from './callOutIcon.png';

const defaultFormValues = {
  name: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  numberOfPowerwalls: '',
  interest: '',

  isHomeowner: false,
};

const baseValidationSchema = {
  name: Yup.string().required('Required'),
  email: Yup.string().required('Enter a valid e-mail address').email('Enter a valid e-mail address'),
  phone: Yup.string().required('Enter your phone number').matches(PhoneRegex, 'Enter your phone number'),
  zip: Yup.string().required('Enter your ZIP code').matches(/\d{5}/, 'Enter your ZIP code'),
  isHomeowner: Yup.boolean().oneOf([true], 'You must be the homeowner')
};

const LeadFormV3 = (props) => {
  const isInEditor = typeof window !== `undefined` ? window.location.pathname.startsWith('/editor') : false;

  const [errorMessage, setErrorMessage] = useState('');
  const [submitState, setSubmitState] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [isCallCenterOpen, setIsCallCenterOpen] = useState(false);
  const { call_center_schedule } = props.blok;
  const isProd = typeof window !== `undefined` && window.location.origin.indexOf("swellenergy.com") > -1 && window.location.origin.indexOf("sbx") === -1;

  useEffect(() => {
    if (!call_center_schedule) {
      setIsCallCenterOpen(false);
      return;
    }

    try {
      const schedule = JSON.parse(call_center_schedule);
      const hasMatch = schedule.schedule.some(s => {
        const isAHoliday =
          fedHolidays.isAHoliday(undefined, { shiftSaturdayHolidays: false, shiftSundayHolidays: false }) ||
          fedHolidays.isAHoliday(undefined, { shiftSaturdayHolidays: true, shiftSundayHolidays: false }) ||
          fedHolidays.isAHoliday(undefined, { shiftSaturdayHolidays: false, shiftSundayHolidays: true }) ||
          fedHolidays.isAHoliday(undefined, { shiftSaturdayHolidays: true, shiftSundayHolidays: true });

        if (isAHoliday) {
          return false;
        }

        const now = moment().tz(s.timezone);

        if (s.days.indexOf(now.day()) === -1) {
          return false;
        }

        const open = moment().tz(s.timezone).set("hour", s.open).set("minute", 0).set("second", 0);
        const close = moment().tz(s.timezone).set("hour", s.close).set("minute", 0).set("second", 0);

        return open < now && close > now;
      });

      setIsCallCenterOpen(hasMatch);
    } catch (err) {
      console.error(err);
      setIsCallCenterOpen(false);
    }
  }, [call_center_schedule]);

  let additionalValidation = {};

  if (props.blok.show_powerwall_dropdown) {
    additionalValidation.numberOfPowerwalls = Yup.string().required('Required');
  }

  if (props.blok.show_interest_dropdown && props.blok.interest_required) {
    additionalValidation.interest = Yup.string().required('Required');
  }

  if (props.blok.show_address_entry && props.blok.address_required) {
    additionalValidation.address = Yup.string().required('Enter your street address.');
    additionalValidation.city = Yup.string().required('Enter your city.');
    additionalValidation.state = Yup.string().required('Enter your state.');
  }

  const onCallNow = () => {
    try {
      metrics.trackCallNowButton(props.blok.utm_content || '');
    } catch (e) { }

    return true;
  }

  const onSubmit = async (values) => {
    setSubmitState('');

    if (!values.email && !values.phone) {
      setSubmitState('failed');
      setErrorMessage('Please provide either your email address or phone number (or both).');
      return;
    }

    try {

      let utmContent = props.blok.utm_content || '';
      const leadSourceAccounts = props.blok.lead_source_account ? JSON.parse(props.blok.lead_source_account) : { dev: '0016100000fehkAAAQ', prod: '0014M00001k8A7fQAE' };

      let promotion = null;

      if (props.blok.promotion) {
        const promotions = JSON.parse(props.blok.promotion);
        promotion = isProd ? promotions.prod : promotions.dev;
      }

      const notes = values.numberOfPowerwalls ? `How many batteries do you have, or plan to have, installed? ${values.numberOfPowerwalls}` : '';
      const customerType = props.blok.customer_type || 'residential';

      let additionalValues = {
        utmContent,
        leadSourceMedium: props.blok.lead_source_medium || 'Swell Energy',
        leadSource: props.blok.lead_source || 'Online Media',
        leadSourceAccount: isProd ? leadSourceAccounts.prod : leadSourceAccounts.dev,
        promotion,
        notes,
        customerType,
      };

      if (props.blok.force_state) {
        additionalValues.state = props.blok.force_state;
      }

      await leads.submitLead({
        ...values,
        ...additionalValues,
      });

      metrics.trackLeadFormSubmit(window.location.origin + window.location.pathname, 'LeadFormV3', utmContent);

      if (props.blok.track_bing_lead_event && typeof window !== 'undefined') {
        window.uetq = window.uetq || [];
        try {
          window.uetq.push('event', 'Lead', { 'event_category': 'Lead', 'event_label': 'Button', 'event_value': 1 });
        } catch (uee) {
          Sentry.captureException(uee);
        }
      }

      if (props.blok.track_fb_lead_event && typeof window !== 'undefined') {
        try {
          window.fbq('track', 'Lead');
        } catch (fberr) {
          Sentry.captureException(fberr);
        }
      }

      if (props.blok.submit_gtag_conversion_event_isaacfrank && props.blok.isaacfrank_gtag_conversion_id && typeof window !== 'undefined' && window && window.gtag) {
        const gtagId = props.blok.isaacfrank_gtag_conversion_id || 'AW-941872757/qxwbCIj559EBEPWsj8ED';

        try {
          window.gtag('event', 'conversion', { 'send_to': gtagId });
        } catch (gee) {
          Sentry.captureException(gee);
        }
      }

      setSubmitState('success');
    } catch (e) {
      Sentry.captureException(e, {
        lead: values
      });
      setErrorMessage('We had a problem processing your request. Please try again.');
      setSubmitState('failed');
    }
  };

  const containerClasses = ['lfv3'];
  containerClasses.push('theme-' + props.blok.theme);

  const homeownerPrompt = props.blok.homeowner_prompt || 'I own my home';

  const validationSchema = Yup.object().shape({
    ...baseValidationSchema,
    ...additionalValidation
  });

  return (
    <SbEditable content={props.blok}>
      <div className={containerClasses.join(' ')}>
        {
          submitState !== 'success' &&
          !(isInEditor && props.blok.preview_success_content) &&
          <Formik
            initialValues={defaultFormValues}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
          >
            {
              formik => {
                const submitButtonText = props.blok.submit_button_text || 'Submit';
                const submittingButtonText = props.blok.submitting_button_text || 'Submitting...';
                const buttonText = (formik.isSubmitting ? submittingButtonText : submitButtonText);
                const buttonStyle = props.blok.button_style || 'blue';
                const showOriginalInstaller = props.blok.show_prompt_original_installer &&
                  props.blok.original_installer_options &&
                  formik.values.numberOfPowerwalls &&
                  formik.values.numberOfPowerwalls !== '0';
                const originalInstallerOptions = JSON.parse(props.blok.original_installer_options || '[]').map(v => {
                  return {
                    name: v.name,
                    value: isProd ? v.prod : v.dev
                  }
                });

                let submitCTA = <button className={`btn-${buttonStyle}`} disabled={formik.isSubmitting} type="submit">{buttonText}</button>;

                if (props.blok.button_options === 'submit_or_call') {
                  if (!call_center_schedule) {
                    submitCTA = <div style={{ backgroundColor: 'red', color: 'white' }}>Select a Call Center Schedule if using 'Submit or Call' option</div>;
                  } else if (!props.blok.call_button_phone_number) {
                    submitCTA = <div style={{ backgroundColor: 'red', color: 'white' }}>Enter a Phone Number if using 'Submit or Call' option</div>;
                  } else {
                    if (isCallCenterOpen || isInEditor) {
                      const fieldsetClassName = `theme-${props.blok.submit_or_call_theme || 'blue'}`;

                      submitCTA = (
                        <div className='submit_or_call_cta'>
                          <div className='desktop'>
                            <button className={`btn-${buttonStyle}`} disabled={formik.isSubmitting} type="submit">{buttonText}</button>
                          </div>
                          <div className='mobile'>
                            <fieldset className={fieldsetClassName}>
                              <legend>Get a FREE Quote</legend>
                              <a className={`link-btn btn-${buttonStyle} first`} href={`tel:${props.blok.call_button_phone_number}`} onClick={() => onCallNow()}>
                                <img src={CallOutIcon} alt="Call Swell" />
                                Call Swell Now
                              </a>
                              <button className={`btn-${buttonStyle}`} disabled={formik.isSubmitting} type="submit">
                                <img src={CallInIcon} alt="We Call You" />
                                We&apos;ll Call You
                              </button>
                            </fieldset>
                          </div>
                        </div>
                      );
                    }
                  }
                }

                return (
                  <Form className="input-form">
                    {props.blok.above_form_content && props.blok.above_form_content.map((blok) => React.createElement(Components(blok.component), { key: blok._uid, blok: blok }))}
                    <TrackFormDirty formComponent='LeadFormV3' isDirty={isDirty} setIsDirty={setIsDirty} />
                    <div className='fields'>
                      <div className="field">
                        <Field name="name" placeholder="Full Name" />
                        <ErrorMessage className="error" component="div" name="name" />
                      </div>
                      <div className="field">
                        <Field name="phone" placeholder="Mobile Phone">
                          {({ field }) => (
                            <InputMask {...field} mask="(999) 999-9999" placeholder="Mobile Phone" type="text" alwaysShowMask={false} />
                          )}
                        </Field>
                        <ErrorMessage className="error" component="div" name="phone" />
                      </div>
                      <div className="field">
                        <Field name="email" placeholder="Email" />
                        <ErrorMessage className="error" component="div" type="email" name="email" />
                      </div>
                      {props.blok.show_address_entry && (
                        <>
                          <div className="field">
                            <Field name="address" placeholder="Street Address" />
                            <ErrorMessage className="error" component="div" name="address" />
                          </div>
                          <div className="field">
                            <Field name="city" placeholder="City" />
                            <ErrorMessage className="error" component="div" name="city" />
                          </div>
                          <div className="field">
                            <Field name="state" placeholder="State" />
                            <ErrorMessage className="error" component="div" name="state" />
                          </div>
                        </>
                      )}
                      <div className="field">
                        <Field name="zip" placeholder="Zip Code">
                          {({ field }) => (
                            <InputMask {...field} mask="99999" placeholder="Zip Code" type="text" alwaysShowMask={false} />
                          )}
                        </Field>
                        <ErrorMessage className="error" component="div" name="zip" />
                      </div>
                      {
                        props.blok.show_interest_dropdown &&
                        <div className="field mt-2 mb-2">
                          <Field as="select" name="interest">
                            <option value=''>I am interested in...</option>
                            <option value='Battery with New Solar'>Solar and Home Battery</option>
                            <option value='Battery Only'>Home Battery Only</option>
                            <option value='Battery with Existing Solar'>Home Battery with Existing Solar</option>
                            <option value='Solar Only'>Solar Power Only</option>
                            <option value='Virtual Power Plant'>Virtual Power Plant</option>
                            <option value='Something Else'>Something Else</option>
                          </Field>
                          <ErrorMessage className="error" component="div" name="interest" />
                        </div>
                      }
                      {
                        props.blok.show_powerwall_dropdown &&
                        <div className="field mt-2 mb-2">
                          <label>How many batteries do you have, or plan to have, installed?</label>
                          <Field as="select" name="numberOfPowerwalls">
                            <option value=''>(select)</option>
                            <option value='0'>0</option>
                            <option value='1'>1</option>
                            <option value='2'>2</option>
                            <option value='3+'>3+</option>
                          </Field>
                          <ErrorMessage className="error" component="div" name="numberOfPowerwalls" />
                        </div>
                      }
                      {
                        showOriginalInstaller &&
                        <div className="field mt-2 mb-2">
                          <label>Who is your installer?</label>
                          <Field as="select" name="originalInstaller">
                            <option>(select)</option>
                            {originalInstallerOptions.map((v, idx) => <option key={`original-installer-${idx}`} value={v.value}>{v.name}</option>)}
                            <option value=''>Another Company</option>
                          </Field>
                        </div>
                      }
                      <div className="field-checkbox">
                        <Field name="isHomeowner" type="checkbox" id="isHomeowner" />
                        <label htmlFor="isHomeowner">{homeownerPrompt}</label>
                        <ErrorMessage className="error" component="div" name="isHomeowner" />
                      </div>
                    </div>
                    {
                      submitState === 'failed' &&
                      <div className='error'>{errorMessage}</div>
                    }
                    {submitCTA}
                    <span className='disclaimer'>
                      By submitting this form, I agree to the{' '}
                      <a href="/privacy-policy">Privacy Policy</a> and consent to be contacted by or
                      on behalf of Swell Energy, including by email, calls, and text messages to any
                      telephone number that I provide, about my interest in Swell Energy products
                      and services. I understand my consent is not required to purchase.
                    </span>
                    {props.blok.below_form_content && props.blok.below_form_content.map((blok) => React.createElement(Components(blok.component), { key: blok._uid, blok: blok }))}
                  </Form>
                )
              }
            }
          </Formik>
        }
        {
          (
            submitState === 'success' ||
            (isInEditor && props.blok.preview_success_content)
          ) &&
          <>
            <div className="success">
              {props.blok.success_content && props.blok.success_content.map((blok) => React.createElement(Components(blok.component), { key: blok._uid, blok: blok }))}
            </div>
          </>
        }
      </div>
    </SbEditable>
  )
}

export default LeadFormV3;